import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import CountrySearchSelect from '../common/CountrySearchSelect';
import { useAuth } from '../../context/AuthContext';
import { useCountries } from '../../hooks/useCountries';
import { apiDelete, apiGet, apiUpload, getApiBase } from '../../services/api';

const BIO_MAX = 1000;

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  country: '',
  city: '',
  professionalTitle: '',
  bio: '',
};

export default function CandidateProfileTab({ token, handleAuthError }) {
  const navigate = useNavigate();
  const { logout, refreshUser } = useAuth();
  const { countries, loading: countriesLoading } = useCountries();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasCv, setHasCv] = useState(false);
  const [cvFilename, setCvFilename] = useState(null);
  const [cvUploadedAt, setCvUploadedAt] = useState(null);
  const [removingCv, setRemovingCv] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [hiddenCompanies, setHiddenCompanies] = useState([]);
  const [loadingHidden, setLoadingHidden] = useState(false);
  const [unhidingId, setUnhidingId] = useState(null);

  const loadProfile = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/v1/candidate/profile', token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not load profile.');
        return;
      }
      const json = await res.json();
      const profile = json.data ?? {};
      setForm({
        firstName: profile.first_name ?? '',
        lastName: profile.last_name ?? '',
        country: profile.country ?? '',
        city: profile.city ?? '',
        professionalTitle: profile.professional_title ?? '',
        bio: profile.bio ?? '',
      });
      setHasCv(Boolean(profile.has_cv));
      setCvFilename(profile.cv_filename ?? null);
      setCvUploadedAt(profile.cv_uploaded_at ?? null);
    } catch {
      setError('Could not load profile.');
    } finally {
      setLoading(false);
    }
  }, [token, handleAuthError]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const loadHiddenCompanies = useCallback(async () => {
    if (!token) return;
    setLoadingHidden(true);
    try {
      const res = await apiGet('/api/v1/candidate/hidden-companies', token);
      if (handleAuthError(res)) return;
      if (!res.ok) return;
      const json = await res.json();
      setHiddenCompanies(Array.isArray(json.data) ? json.data : []);
    } catch {
      setHiddenCompanies([]);
    } finally {
      setLoadingHidden(false);
    }
  }, [token, handleAuthError]);

  useEffect(() => {
    loadHiddenCompanies();
  }, [loadHiddenCompanies]);

  const handleUnhideCompany = async (companyId) => {
    if (!token) return;
    setUnhidingId(companyId);
    try {
      const res = await apiDelete(`/api/v1/candidate/companies/${companyId}/hide`, token);
      if (handleAuthError(res)) return;
      if (res.ok) {
        setHiddenCompanies((prev) => prev.filter((c) => c.id !== companyId));
      }
    } finally {
      setUnhidingId(null);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      const map = {
        firstName: 'first_name',
        lastName: 'last_name',
        country: 'country',
        city: 'city',
        professionalTitle: 'professional_title',
        bio: 'bio',
      };
      delete next[map[key] || key];
      delete next.cv;
      return next;
    });
  };

  const handleCvDownload = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${getApiBase()}/api/v1/candidate/profile/cv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not download CV.');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError('Could not download CV.');
    }
  };

  const handleDeleteAccount = async () => {
    if (!token) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await fetch(`${getApiBase()}/api/v1/candidate/account`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      if (handleAuthError(res)) return;
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setDeleteError(data?.message || 'Could not delete account. Please try again.');
        return;
      }
      try {
        sessionStorage.setItem('rwm_flash_message', 'Your account has been deleted.');
      } catch {
        // sessionStorage unavailable
      }
      logout();
      setShowDeleteConfirm(false);
      navigate('/');
    } catch {
      setDeleteError('Could not delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleRemoveCv = async () => {
    if (!token) return;
    setRemovingCv(true);
    setError('');
    try {
      const res = await fetch(`${getApiBase()}/api/v1/candidate/profile/cv`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not remove CV. Please try again.');
        return;
      }
      setHasCv(false);
      setCvFilename(null);
      setCvUploadedAt(null);
      setCvFile(null);
      if (refreshUser) refreshUser();
    } catch {
      setError('Could not remove CV. Please try again.');
    } finally {
      setRemovingCv(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;
    setSaving(true);
    setError('');
    setSuccess(false);
    setFieldErrors({});

    const formData = new FormData();
    formData.append('first_name', form.firstName.trim());
    formData.append('last_name', form.lastName.trim());
    if (form.country) {
      formData.append('country', form.country);
    }
    if (form.city) {
      formData.append('city', form.city.trim());
    }
    if (form.professionalTitle) {
      formData.append('professional_title', form.professionalTitle.trim());
    }
    if (form.bio) {
      formData.append('bio', form.bio.trim());
    }
    if (cvFile) {
      formData.append('cv', cvFile);
    }

    try {
      const res = await apiUpload('/api/v1/candidate/profile', formData, token);
      if (handleAuthError(res)) return;
      const data = await res.json().catch(() => ({}));
      if (res.status === 422) {
        const errors = data?.errors || {};
        const mapped = {};
        Object.keys(errors).forEach((key) => {
          mapped[key] = Array.isArray(errors[key]) ? errors[key][0] : errors[key];
        });
        setFieldErrors(mapped);
        return;
      }
      if (!res.ok) {
        setError(data?.message || 'Could not save profile.');
        return;
      }
      setHasCv(Boolean(data?.data?.has_cv));
      setCvFilename(data?.data?.cv_filename ?? null);
      setCvUploadedAt(data?.data?.cv_uploaded_at ?? null);
      setCvFile(null);
      setSuccess(true);
      if (refreshUser) refreshUser();
    } catch {
      setError('Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" data-testid="rwm-profile-loading" />
      </div>
    );
  }

  return (
    <Form className="rwm-profile__form" onSubmit={handleSubmit}>
      {success ? (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccess(false)}
          className="mb-3"
          data-testid="rwm-profile-success"
        >
          Profile updated successfully.
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="danger" className="mb-3" data-testid="rwm-profile-error">
          {error}
        </Alert>
      ) : null}

      <h2 className="rwm-profile__section-title">Personal Info</h2>
      <Row className="g-3 mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label htmlFor="rwm-profile-first-name">First Name *</Form.Label>
            <Form.Control
              id="rwm-profile-first-name"
              value={form.firstName}
              onChange={(e) => updateField('firstName', e.target.value)}
              maxLength={100}
              required
              isInvalid={Boolean(fieldErrors.first_name)}
              data-testid="rwm-profile-first-name"
            />
            <Form.Control.Feedback type="invalid">{fieldErrors.first_name}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label htmlFor="rwm-profile-last-name">Last Name *</Form.Label>
            <Form.Control
              id="rwm-profile-last-name"
              value={form.lastName}
              onChange={(e) => updateField('lastName', e.target.value)}
              maxLength={100}
              required
              isInvalid={Boolean(fieldErrors.last_name)}
              data-testid="rwm-profile-last-name"
            />
            <Form.Control.Feedback type="invalid">{fieldErrors.last_name}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Country</Form.Label>
            <CountrySearchSelect
              countries={countries}
              loading={countriesLoading}
              value={form.country}
              onChange={(name) => updateField('country', name)}
              testId="rwm-profile-country"
            />
            {fieldErrors.country ? (
              <div className="text-danger small mt-1">{fieldErrors.country}</div>
            ) : null}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label htmlFor="rwm-profile-city">City</Form.Label>
            <Form.Control
              id="rwm-profile-city"
              value={form.city}
              onChange={(e) => updateField('city', e.target.value)}
              maxLength={100}
              isInvalid={Boolean(fieldErrors.city)}
              data-testid="rwm-profile-city"
            />
            <Form.Control.Feedback type="invalid">{fieldErrors.city}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <h2 className="rwm-profile__section-title">Professional Info</h2>
      <Form.Group className="mb-3">
        <Form.Label htmlFor="rwm-profile-title">Professional Title</Form.Label>
        <Form.Control
          id="rwm-profile-title"
          value={form.professionalTitle}
          onChange={(e) => updateField('professionalTitle', e.target.value)}
          maxLength={150}
          isInvalid={Boolean(fieldErrors.professional_title)}
          data-testid="rwm-profile-title"
        />
        <Form.Control.Feedback type="invalid">{fieldErrors.professional_title}</Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-4">
        <Form.Label htmlFor="rwm-profile-bio">Bio</Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          id="rwm-profile-bio"
          value={form.bio}
          onChange={(e) => updateField('bio', e.target.value)}
          maxLength={BIO_MAX}
          isInvalid={Boolean(fieldErrors.bio)}
          data-testid="rwm-profile-bio"
        />
        <Form.Text className={form.bio.length > BIO_MAX ? 'text-danger' : 'text-muted'}>
          {form.bio.length} / {BIO_MAX} characters
        </Form.Text>
        <Form.Control.Feedback type="invalid">{fieldErrors.bio}</Form.Control.Feedback>
      </Form.Group>

      <div className="rwm-profile__cv-section">
        <h2 className="rwm-profile__section-title">CV</h2>
        {hasCv ? (
          <div className="mb-2">
            <Badge bg="success" className="me-2" data-testid="rwm-profile-cv-status">
              CV uploaded ✓
            </Badge>
            {cvFilename ? (
              <span className="text-muted small" data-testid="rwm-profile-cv-filename">
                {cvFilename}
              </span>
            ) : null}
            {cvUploadedAt ? (
              <div className="text-muted small mt-1" data-testid="rwm-profile-cv-uploaded-at">
                Uploaded {new Date(cvUploadedAt).toLocaleDateString('en-GB', {
                  day: 'numeric', month: 'long', year: 'numeric',
                })}
              </div>
            ) : null}
            <div className="mt-2">
              <Button
                variant="link"
                className="p-0 me-3"
                onClick={handleCvDownload}
                data-testid="rwm-profile-cv-download"
              >
                Download my CV
              </Button>
              <Button
                variant="link"
                className="p-0 text-danger"
                onClick={handleRemoveCv}
                disabled={removingCv}
                data-testid="rwm-profile-cv-remove"
              >
                {removingCv ? 'Removing…' : 'Remove CV'}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-muted small mb-2" data-testid="rwm-profile-cv-status">
            No CV uploaded yet.
          </p>
        )}
        <Form.Group className="mb-2">
          <Form.Label htmlFor="rwm-profile-cv-upload">
            {hasCv ? 'Replace CV (PDF, max 5MB)' : 'Upload CV (PDF, max 5MB)'}
          </Form.Label>
          <Form.Control
            type="file"
            id="rwm-profile-cv-upload"
            accept="application/pdf,.pdf"
            onChange={(e) => setCvFile(e.target.files?.[0] ?? null)}
            data-testid="rwm-profile-cv-upload"
          />
          {fieldErrors.cv ? <div className="text-danger small mt-1">{fieldErrors.cv}</div> : null}
        </Form.Group>
      </div>

      <Button
        type="submit"
        variant="info"
        disabled={saving}
        className="mt-4"
        data-testid="rwm-profile-save"
      >
        {saving ? 'Saving…' : 'Save Profile'}
      </Button>

      <div className="rwm-profile__export mt-5 p-4 border rounded">
        <h5 className="rwm-profile__export-title">Your Data (GDPR)</h5>
        <p className="text-muted small mb-3">
          Download a copy of your profile, applications, saved jobs, searches, and alerts.
        </p>
        {exportError ? (
          <Alert variant="danger" className="mb-3" data-testid="rwm-profile-data-export-error">
            {exportError}
          </Alert>
        ) : null}
        <Button
          variant="outline-info"
          disabled={exporting}
          onClick={async () => {
            if (!token) return;
            setExporting(true);
            setExportError('');
            try {
              const res = await fetch(`${getApiBase()}/api/v1/candidate/data-export`, {
                headers: {
                  Accept: 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              });
              if (handleAuthError(res)) return;
              if (!res.ok) {
                setExportError('Could not download your data. Please try again.');
                return;
              }
              const blob = await res.blob();
              const disposition = res.headers.get('Content-Disposition') || '';
              const match = disposition.match(/filename="([^"]+)"/);
              const filename = match?.[1] || `rwm-data-export-${Date.now()}.json`;
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = filename;
              document.body.appendChild(link);
              link.click();
              link.remove();
              URL.revokeObjectURL(url);
            } catch {
              setExportError('Could not download your data. Please try again.');
            } finally {
              setExporting(false);
            }
          }}
          data-testid="rwm-profile-data-export-btn"
        >
          {exporting ? (
            <>
              <Spinner
                animation="border"
                size="sm"
                className="me-2"
                data-testid="rwm-profile-data-export-loading"
              />
              Downloading…
            </>
          ) : (
            'Download My Data (GDPR)'
          )}
        </Button>
      </div>

      <div className="rwm-profile__hidden-companies mt-5 p-4 border rounded" data-testid="rwm-hidden-companies-section">
        <h5 className="rwm-profile__hidden-title">Hidden Companies</h5>
        <p className="text-muted small mb-3">
          Jobs from these companies are hidden from Browse Jobs and your homepage feed.
        </p>
        {loadingHidden ? (
          <div className="d-flex align-items-center gap-2 text-muted" data-testid="rwm-hidden-companies-loading">
            <Spinner animation="border" size="sm" />
            <span>Loading…</span>
          </div>
        ) : hiddenCompanies.length === 0 ? (
          <p className="text-muted mb-0" data-testid="rwm-hidden-companies-empty">
            No hidden companies.
          </p>
        ) : (
          <ul className="list-unstyled mb-0">
            {hiddenCompanies.map((company) => (
              <li
                key={company.id}
                className="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom"
                data-testid={`rwm-hidden-company-${company.id}`}
              >
                <span data-testid={`rwm-hidden-company-name-${company.id}`}>{company.name}</span>
                <Button
                  variant="outline-info"
                  size="sm"
                  disabled={unhidingId === company.id}
                  onClick={() => handleUnhideCompany(company.id)}
                  data-testid={`rwm-unhide-company-${company.id}`}
                >
                  {unhidingId === company.id ? 'Unhiding…' : 'Unhide'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rwm-profile__danger-zone mt-5 p-4 border border-danger rounded">
        <h5 className="text-danger rwm-profile__danger-title">Danger Zone</h5>
        <p className="text-muted small mb-3">
          Permanently delete your account and all associated data. This action cannot be undone.
          Your job applications will be anonymized.
        </p>
        <Button
          variant="outline-danger"
          onClick={() => {
            setDeleteError('');
            setShowDeleteConfirm(true);
          }}
          data-testid="rwm-profile-delete-account-btn"
        >
          Delete My Account
        </Button>
      </div>

      <Modal
        show={showDeleteConfirm}
        onHide={() => !deleting && setShowDeleteConfirm(false)}
        centered
        data-testid="rwm-profile-delete-confirm-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Are you sure you want to permanently delete your account? This cannot be undone. All
            your applications will be anonymized.
          </p>
          {deleteError ? (
            <Alert variant="danger" className="mt-3 mb-0" data-testid="rwm-profile-delete-error">
              {deleteError}
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            disabled={deleting}
            onClick={() => setShowDeleteConfirm(false)}
            data-testid="rwm-profile-delete-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={deleting}
            onClick={handleDeleteAccount}
            data-testid="rwm-profile-delete-confirm-btn"
          >
            {deleting ? 'Deleting…' : 'Yes, Delete My Account'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Form>
  );
}
